import type { Section } from '../types';

/**
 * Agenda strings (migration 017). Kept in its own file so the schedule builder
 * and the rooms builder never edit the same lines of ops.ts.
 *
 * Spanish is for the crew: third grade reading level, one instruction per line,
 * tu form, Panamanian words. Every key has its English twin. The owner edit
 * screen is drawn in English on purpose and asks for the English side.
 */
export const agenda = {
  'ops.agenda.title': { es: 'Agenda', en: 'Schedule' },
  'ops.agenda.nav.today': { es: 'Hoy', en: 'Today' },
  'ops.agenda.nav.week': { es: 'Semana', en: 'Week' },
  'ops.agenda.nav.edit': { es: 'Editar', en: 'Edit' },
  'ops.agenda.nav.label': { es: 'Partes de la agenda', en: 'Schedule sections' },
  'ops.agenda.loading': { es: 'Cargando la agenda...', en: 'Loading the schedule...' },
  'ops.agenda.no_tasks': {
    es: 'Todavía no hay tareas en este teléfono. Conéctate una vez para bajarlas.',
    en: 'There are no tasks on this phone yet. Connect once to download them.',
  },

  'ops.agenda.unknown': {
    es: 'Este teléfono no sabe si hay huéspedes. Conéctate una vez. Mientras tanto, solo trabajo suave.',
    en: 'This phone does not know if guests are here. Connect once. Until then, light work only.',
  },
  'ops.agenda.stale': {
    es: 'Sin señal. Esta lista es de {time}.',
    en: 'No signal. This list is from {time}.',
  },

  'ops.agenda.banner.occupied': { es: 'Hay huéspedes en la isla', en: 'Guests are on the island' },
  'ops.agenda.banner.empty': { es: 'La isla está vacía', en: 'The island is empty' },
  'ops.agenda.banner.leave_on': { es: 'Se van el {date}.', en: 'They leave on {date}.' },
  'ops.agenda.banner.next_arrival': { es: 'Llegan huéspedes el {date}.', en: 'Guests arrive on {date}.' },
  'ops.agenda.banner.none_booked': { es: 'No hay huéspedes apuntados.', en: 'No guests are booked.' },
  'ops.agenda.banner.empty_days': { es: 'Días vacíos: {days}.', en: 'Empty days: {days}.' },
  'ops.agenda.banner.light_only': {
    es: 'Hoy solo trabajo suave y sin ruido.',
    en: 'Only quiet, light work today.',
  },
  'ops.agenda.banner.deep_ok': {
    es: 'Hoy se puede hacer trabajo pesado.',
    en: 'Deep work is allowed today.',
  },
  'ops.agenda.banner.arrival': {
    es: 'Hoy llegan huéspedes. Nada de ruido ni trabajo pesado.',
    en: 'Guests arrive today. No noise and no deep work.',
  },
  'ops.agenda.banner.departure': {
    es: 'Hoy se van los huéspedes. Cuando salgan, la isla queda vacía.',
    en: 'The guests leave today. When they are gone, the island is empty.',
  },
  'ops.agenda.banner.turnover': {
    es: 'Hoy se van unos huéspedes y llegan otros.',
    en: 'One group leaves and another arrives today.',
  },

  'ops.agenda.list.title': { es: 'Para hoy', en: 'For today' },
  'ops.agenda.list.nothing': {
    es: 'No hay nada para hoy. Buen trabajo.',
    en: 'Nothing is due today. Nice work.',
  },
  'ops.agenda.list.left': { es: 'Faltan {min} min', en: '{min} min left' },
  'ops.agenda.list.done_button': { es: 'Hecho', en: 'Done' },
  'ops.agenda.list.mark': { es: 'Marcar como hecho: {name}', en: 'Mark as done: {name}' },
  'ops.agenda.list.done_at': { es: 'Hecho a las {time}', en: 'Done at {time}' },
  'ops.agenda.list.minutes': { es: '{min} min', en: '{min} min' },
  'ops.agenda.list.ticked': { es: 'Marcado: {name}', en: 'Marked: {name}' },
  'ops.agenda.list.undo': { es: 'Deshacer', en: 'Undo' },
  'ops.agenda.list.undo_late': {
    es: 'Ya se guardó en la isla. No se puede deshacer.',
    en: 'It is already saved on the island. It cannot be undone.',
  },
  'ops.agenda.list.does_not_fit': {
    es: 'No cabe en los días vacíos',
    en: 'Does not fit in the empty days',
  },
  'ops.agenda.list.never': { es: 'Nunca se ha hecho', en: 'Never done' },
  'ops.agenda.list.ahead': { es: 'Si te sobra tiempo', en: 'If you have time left' },
  'ops.agenda.list.ahead_hint': {
    es: 'Todavía no toca, pero ya casi.',
    en: 'Not due yet, but nearly.',
  },
  'ops.agenda.list.save_failed': {
    es: 'No se pudo guardar en el teléfono. Intenta otra vez.',
    en: 'It could not be saved on the phone. Try again.',
  },

  'ops.agenda.pending.title': { es: 'Pendientes para la isla vacía', en: 'Waiting for an empty island' },
  'ops.agenda.pending.starts': {
    es: 'Esto se hace cuando no haya huéspedes. La isla queda vacía el {date}.',
    en: 'This waits until the guests are gone. The island is empty from {date}.',
  },
  'ops.agenda.pending.days': { es: 'Serán {days} días vacíos.', en: 'That is {days} empty days.' },
  'ops.agenda.pending.day': { es: 'Será 1 día vacío.', en: 'That is 1 empty day.' },
  'ops.agenda.pending.open': {
    es: 'Después no hay huéspedes apuntados.',
    en: 'After that no guests are booked.',
  },
  'ops.agenda.pending.none': {
    es: 'No hay trabajo pesado esperando.',
    en: 'No deep work is waiting.',
  },
  'ops.agenda.pending.total': {
    es: 'En total son {min} min de trabajo pesado.',
    en: 'That is {min} min of deep work in all.',
  },
  'ops.agenda.pending.warn_fit': {
    es: 'Hay trabajo atrasado que no cabe. Avísale a Tim para que deje más días vacíos.',
    en: 'Some overdue work does not fit. Tell Tim so he keeps more days empty.',
  },
  'ops.agenda.pending.warn_block': {
    es: 'Hay trabajo muy atrasado, el doble de lo normal. Avísale a Tim para que deje días vacíos.',
    en: 'Some work is at twice its interval. Tell Tim so he blocks empty days.',
  },

  'ops.agenda.status.ok': { es: 'Al día', en: 'On track' },
  'ops.agenda.status.due_soon': { es: 'Pronto', en: 'Soon' },
  'ops.agenda.status.due': { es: 'Toca hoy', en: 'Due' },
  'ops.agenda.status.overdue': { es: 'Atrasada', en: 'Overdue' },
  'ops.agenda.class.deep': { es: 'Pesado', en: 'Deep' },
  'ops.agenda.class.light': { es: 'Suave', en: 'Light' },
  'ops.agenda.class.any': { es: 'Revisión', en: 'Check' },
  'ops.agenda.esc.tell_tim': { es: 'Avísale a Tim', en: 'Tell Tim' },
  'ops.agenda.esc.block_days': { es: 'Hay que dejar días vacíos', en: 'Block empty days' },

  'ops.agenda.tally.title': { es: 'Cuentas de la semana', en: 'Weekly tally' },
  'ops.agenda.tally.done': {
    es: 'Esta semana se hicieron {count} trabajos.',
    en: '{count} jobs done this week.',
  },
  'ops.agenda.tally.minutes': { es: '{min} min de trabajo.', en: '{min} min of work.' },
  'ops.agenda.tally.overdue': { es: '{count} atrasadas', en: '{count} overdue' },
  'ops.agenda.tally.due': { es: '{count} tocan hoy', en: '{count} due' },
  'ops.agenda.tally.never': { es: '{count} sin hacerse nunca', en: '{count} never done' },
  'ops.agenda.tally.progress': { es: '{days} de {interval} días', en: '{days} of {interval} days' },
  'ops.agenda.tally.every': { es: 'Cada {days} días', en: 'Every {days} days' },
  'ops.agenda.tally.every_one': { es: 'Todos los días', en: 'Every day' },
  'ops.agenda.tally.today': { es: 'Hecha hoy', en: 'Done today' },
  'ops.agenda.tally.last': { es: 'Hace {days} días', en: '{days} days ago' },
  'ops.agenda.tally.last_one': { es: 'Ayer', en: 'Yesterday' },
  'ops.agenda.tally.late_by': { es: '{days} días de atraso', en: '{days} days late' },
  'ops.agenda.tally.late_by_one': { es: '1 día de atraso', en: '1 day late' },
  'ops.agenda.tally.due_in': { es: 'Toca en {days} días', en: 'Due in {days} days' },
  'ops.agenda.tally.due_in_one': { es: 'Toca mañana', en: 'Due tomorrow' },
  'ops.agenda.tally.only_empty': { es: 'Solo con la isla vacía', en: 'Only with the island empty' },

  'ops.agenda.filter.label': { es: 'Ver por lugar', en: 'Show by area' },
  'ops.agenda.filter.all': { es: 'Todo', en: 'All' },
  'ops.agenda.area.hacienda': { es: 'Hacienda', en: 'Hacienda' },
  'ops.agenda.area.bungalow': { es: 'Bungalow', en: 'Bungalow' },
  'ops.agenda.area.pabellon': { es: 'Pabellón', en: 'Pavilion' },
  'ops.agenda.area.casita': { es: 'Casita', en: 'Staff casita' },
  'ops.agenda.area.muelles': { es: 'Muelles', en: 'Docks' },
  'ops.agenda.area.island': { es: 'Toda la isla', en: 'Whole island' },

  'ops.agenda.edit.title': { es: 'Editar la agenda', en: 'Edit the schedule' },
  'ops.agenda.edit.lede': {
    es: 'Los cambios le llegan a todo el equipo. Necesitas señal.',
    en: 'Changes reach the whole crew. You need signal.',
  },
  'ops.agenda.edit.only_owner': {
    es: 'Solo un dueño puede editar la agenda.',
    en: 'Only an owner can edit the schedule.',
  },
  'ops.agenda.edit.interval': { es: 'Cada cuántos días', en: 'Every how many days' },
  'ops.agenda.edit.allowed': { es: 'Cuándo se puede', en: 'Allowed when' },
  'ops.agenda.edit.when.empty_only': { es: 'Solo con la isla vacía', en: 'Empty island only' },
  'ops.agenda.edit.when.occupied_ok': { es: 'Solo con huéspedes', en: 'Guest days only' },
  'ops.agenda.edit.when.both': { es: 'Cualquier día', en: 'Any day' },
  'ops.agenda.edit.active': { es: 'Activa', en: 'Active' },
  'ops.agenda.edit.off': { es: 'Apagada', en: 'Switched off' },
  'ops.agenda.edit.show_off': { es: 'Ver también las apagadas', en: 'Show switched off tasks too' },
  'ops.agenda.edit.save': { es: 'Guardar', en: 'Save' },
  'ops.agenda.edit.saved': { es: 'Guardado.', en: 'Saved.' },
  'ops.agenda.edit.failed': { es: 'No se pudo guardar.', en: 'It could not be saved.' },
  'ops.agenda.edit.bad_interval': {
    es: 'Pon un número de días, 1 o más.',
    en: 'Enter a number of days, 1 or more.',
  },
  'ops.agenda.edit.source': { es: 'De dónde salió', en: 'Where it came from' },
  'ops.agenda.edit.minutes': { es: '{min} min', en: '{min} min' },
} as const satisfies Section;
